import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  computed,
  signal
} from "@angular/core";
import { ServiceFormComponent } from "@components/service-form/service-form.component";
import { Servico } from "@interfaces/service";
import { Store } from "@ngrx/store";
import { ModalService } from "@services/modal.service";
import { NfseServiceService } from "@services/nfse-service.service";
import { selectPNCompany } from "@state/selectors/user.selectors";
import { PaginatorState } from "primeng/paginator";
import { Subscription } from "rxjs";

@Component({
  selector: "app-nfse-settings-tab",
  templateUrl: "./nfse-settings-tab.component.html",
  styleUrls: ["./nfse-settings-tab.component.scss"]
})
export class NfseSettingsTabComponent implements OnInit {
  services = signal<Servico[]>([]);
  editing = signal(false);
  toEdit = signal<Servico | null>(null);
  currentPage = signal(1);
  totalItems = signal(0);
  totalPages = signal(0);
  isLoading = signal(false);
  isLoadingServices = signal(false);

  company = this.store.selectSignal(selectPNCompany);

  /** Muda quando a empresa é salva (pré-cadastro pelos CNAEs): recarrega a lista. */
  @Input() set refreshNonce(value: number | null | undefined) {
    if (!value || value === this.lastRefreshNonce) return;
    this.lastRefreshNonce = value;
    if (this.initialized) this.loadServices(1);
  }

  private lastRefreshNonce = 0;
  private initialized = false;

  /** Formulário encadeado (CNAE → LC 116 → NBS → ISS → IBS/CBS), compartilhado
   * com a página "Novo serviço". */
  @ViewChild(ServiceFormComponent) serviceForm?: ServiceFormComponent;

  isEditMode = computed(() => !!this.toEdit());
  buttonText = computed(() => (this.isEditMode() ? "Atualizar" : "Salvar"));
  buttonIcon = computed(() => (this.isEditMode() ? "pi-refresh" : "pi-save"));
  loadingText = computed(() =>
    this.isEditMode() ? "Atualizando..." : "Salvando..."
  );

  sub$: Subscription[] = [];

  constructor(
    private readonly store: Store,
    private readonly destroyRef: DestroyRef,
    private readonly nfseServiceService: NfseServiceService,
    private readonly modalService: ModalService
  ) {
    this.destroyRef.onDestroy(() => {
      this.sub$.forEach((sub$) => sub$.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.initialized = true;
    queueMicrotask(() => {
      this.loadServices();
    });
  }

  loadServices(page = 1) {
    this.isLoadingServices.set(true);
    this.sub$.push(
      this.nfseServiceService.getServices(page, 6).subscribe({
        next: (response) => {
          this.services.set(response.data);
          this.totalItems.set(response.total);
          this.totalPages.set(response.totalPages);
          this.currentPage.set(response.page);
          this.isLoadingServices.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoadingServices.set(false);
          this.modalService.error(
            err?.error?.message?.message || "Erro ao carregar serviços."
          );
        }
      })
    );
  }

  startEditing(service: Servico) {
    this.editing.set(true);
    this.toEdit.set(service);
  }

  newService() {
    this.editing.set(true);
  }

  close() {
    this.editing.set(false);
    this.toEdit.set(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }

  discardChanges() {
    this.modalService.confirm("Deseja descartar as alterações?", () => {
      this.serviceForm?.reset(this.toEdit());
    });
  }

  submit(payload: Record<string, unknown>) {
    this.isLoading.set(true);
    const serviceToEdit = this.toEdit();
    const isUpdate = this.isEditMode();

    const request$ =
      isUpdate && serviceToEdit
        ? this.nfseServiceService.update(serviceToEdit.id, payload)
        : this.nfseServiceService.save(payload);

    this.sub$.push(
      request$.subscribe({
        next: () => {
          this.isLoading.set(false);
          const message = isUpdate
            ? "Os dados do serviço foram atualizados com sucesso."
            : "Os dados do serviço foram cadastrados com sucesso.";

          this.modalService.success(message, () => {
            this.close();
            // Recarregar serviços após salvar/atualizar
            this.loadServices(this.currentPage());
          });
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.modalService.error(
            err?.error?.message?.message || "Erro ao processar a solicitação."
          );
        }
      })
    );
  }

  /**
   * Ativa/inativa um serviço. Pré-cadastros do CNAE nascem inativos; o
   * nf-server recusa ativar enquanto faltar código, discriminação ou alíquota.
   */
  setAtivo(service: Servico, ativo: boolean) {
    this.sub$.push(
      this.nfseServiceService.setAtivo(service.id, ativo).subscribe({
        next: () => {
          this.modalService.success(
            ativo
              ? "Serviço ativado: ele já pode ser usado na emissão de NFS-e."
              : "Serviço inativado: ele não aparece mais na emissão de NFS-e.",
            () => this.loadServices(this.currentPage())
          );
        },
        error: (err: HttpErrorResponse) => {
          this.modalService.error(
            err?.error?.message?.message ||
              err?.error?.message ||
              "Não foi possível alterar a situação do serviço."
          );
        }
      })
    );
  }

  deleteService(id: string) {
    const handleDeletion = () => {
      this.sub$.push(
        this.nfseServiceService.deleteService(id).subscribe({
          next: () => {
            this.modalService.success(
              "Os dados do serviço foram excluídos com sucesso.",
              () => {
                // Recarregar serviços após excluir
                this.loadServices(this.currentPage());
              }
            );
          },
          error: (err: HttpErrorResponse) => {
            this.modalService.error(err?.error.message.message);
          }
        })
      );
    };

    this.modalService.deletion(
      undefined,
      "Deseja excluir os dados do serviço? Essa ação não poderá ser desfeita.",
      handleDeletion.bind(this),
      undefined,
      {
        label: 'Digite "excluir" para confirmar a exclusão.',
        required: true,
        fieldName: "confirmation",
        isValid: (value) => value === "excluir"
      }
    );
  }

  onPageChange(state: PaginatorState) {
    const newPage = (state.page ?? 0) + 1;
    this.loadServices(newPage);
  }
}
